import React, { useContext, useEffect, useState } from "react";
import { SignalPulse } from "../components/SignalPulse";
import { useParams, useNavigate } from "react-router-dom";
import { Flame, Lock, Eye, Calendar, CalendarClock, ArrowLeft, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { BeaconMedia } from "./BeaconMedia";
import { ExternalLinkButton } from "../components/ExternalLinkButton";
import { beaconLook } from "../lib/beaconStyles";
import { BeaconHero } from "../components/BeaconHero";
import { beaconHit } from "../lib/beaconHit";
import { BeaconSectionCards } from "../components/BeaconSectionCards";
import { BeaconDisclaimer } from "../components/BeaconDisclaimer";
import { VisitorWelcome, SubscriberWelcome, WelcomePreviewToggle } from "../components/BeaconWelcome";
import { useCrypto } from "../context/CryptoContext";
import * as C from "../lib/crypto";
import { useAuth } from "../context/AuthContext";
import { BeaconComments } from "../components/BeaconComments";
import { BeaconUpcoming } from "../components/HearthEvents";
import { ArtGallery, ArtworkInline } from "../components/ArtGallery";
import { themeClass } from "../lib/beaconTemplates";
import { ArtCtx } from "../components/ArtStory";
import { LearnCtx, useLearn, useVideoProgress } from "../lib/learn";
import { LessonComplete } from "../components/LessonComplete";
import { CourseView } from "../components/CourseView";
import { NotesCtx, useLessonNotes, LessonNotes } from "../components/LessonNotes";
import { FileDownload } from "../components/FileDownload";

// "Continue where you left off" for students, plus the choice to share their name with the teacher.
const ContinueBar = ({ b, learn, accent, onGo }) => {
  if (!learn) return null;
  const all = (b.sections || []).flatMap((s) => s.items).filter((it) => it.type !== "artwork" && !it.locked);
  const at = all.findIndex((it) => it.id === learn.last);
  const last = at < 0 ? null : !learn.done.has(all[at].id) ? all[at]
    : all.slice(at + 1).find((it) => !learn.done.has(it.id)) || all.find((it) => !learn.done.has(it.id));
  return (
    <div className="border border-white/10 bg-[#121212] p-3 mb-4 flex flex-wrap items-center gap-3" data-testid="learn-bar">
      {last && <button type="button" onClick={() => onGo(last.id)} data-testid="learn-continue" className="px-3 py-1.5 font-body text-xs font-semibold" style={{ background: accent, color: "#0A0A0A" }}>Continue: {last.title || "your last lesson"}</button>}
      <label className="flex items-center gap-1.5 font-body text-xs text-zinc-400 ml-auto"><input type="checkbox" checked={learn.share} onChange={(e) => learn.setShare(e.target.checked)} data-testid="learn-share-name" /> Let the teacher see my name on my progress</label>
    </div>
  );
};

// Decrypt-on-device text. For plaintext items it just passes through.
const useDecrypted = (enc, encKey, text, iv) => {
  const [out, setOut] = useState(enc ? null : (text || ""));
  useEffect(() => {
    let live = true;
    if (!enc) { setOut(text || ""); return; }
    if (!encKey || !text) { setOut(null); return; }
    C.decryptText(encKey, text, iv).then((p) => { if (live) setOut(p); }).catch(() => { if (live) setOut(null); });
    return () => { live = false; };
  }, [enc, encKey, text, iv]);
  return out;
};

// A drip lesson that hasn't opened yet: title plus when it opens.
const DripLocked = ({ it, accent }) => {
  const at = new Date(it.drip_at);
  const days = Math.max(1, Math.ceil((at - Date.now()) / 864e5));
  const date = (it.drip_date ? new Date(`${it.drip_date}T00:00:00Z`) : at).toLocaleDateString(undefined, { weekday: "short", month: "long", day: "numeric", ...(it.drip_date ? { timeZone: "UTC" } : {}) });
  return (
    <div className="flex items-center gap-2 py-2.5 border-t border-white/5 text-zinc-500 font-body text-sm" data-testid={`beacon-drip-item-${it.id}`}>
      <CalendarClock size={14} style={{ color: accent }} /> <span className="flex-1 truncate">{it.title || "Upcoming lesson"}</span>
      <span className="text-[11px] shrink-0" data-testid={`beacon-drip-when-${it.id}`}>{days === 1 ? "Opens tomorrow" : `Opens in ${days} days`}, on {date}</span>
    </div>
  );
};

const ItemView = ({ it, accent, hFont, hColor, encKey, onEngage, comments, openComments }) => {
  const caption = useDecrypted(it.enc, encKey, it.caption, it.caption_iv);
  const body = useDecrypted(it.enc && it.type === "text", encKey, it.body, it.body_iv);
  const learn = useContext(LearnCtx);
  const vp = useVideoProgress(it, learn);
  if (it.locked && it.drip_at) return <DripLocked it={it} accent={accent} />;
  if (it.locked) return (
    <div className="flex items-center gap-2 py-2.5 border-t border-white/5 text-zinc-500" data-testid="beacon-locked-item">
      <Lock size={14} /> <span className="flex-1">{it.title || <span className="italic opacity-70">Members-only</span>}</span>
      <span className="text-[10px] uppercase tracking-wide">{it.type}</span>
    </div>
  );
  const needsKey = it.enc && !encKey;
  return (
    <div className="py-3 border-t border-white/5" data-testid="beacon-unlocked-item" id={`lesson-${it.id}`} {...vp}
      onPlayCapture={() => onEngage?.(it.id)} onClickCapture={(e) => { learn?.opened(it.id); if (e.target.closest("a")) onEngage?.(it.id); }}>
      {(it.title || it.is_preview) && (
        <div className="flex items-center gap-2 mb-1.5">
          {it.title && <span className="text-white" style={{ fontFamily: hFont, color: hColor }}>{it.title}</span>}
          {it.enc && <Lock size={11} style={{ color: accent }} title="Encrypted: only subscribers can read this" />}
          {it.is_preview && <span className="text-[10px]" style={{ color: accent }}><Eye size={11} className="inline" /> preview</span>}
        </div>
      )}
      {needsKey ? (
        <p className="font-body text-sm text-zinc-500 italic" data-testid="beacon-item-preparing">Preparing your access…</p>
      ) : (
        <>
          {caption && <p className="font-body text-sm text-zinc-400 mb-2 whitespace-pre-wrap">{caption}</p>}
          {it.type === "text" && <p className="font-body text-sm text-zinc-300 whitespace-pre-wrap">{body}</p>}
          {it.type === "artwork" && it.media && <ArtworkInline it={it} encKey={encKey} accent={accent} />}
          {it.type === "image" && it.media && <BeaconMedia path={it.media} type="image" encKey={it.enc ? encKey : null} iv={it.media_iv} className="max-h-96 rounded" />}
          {it.type === "video" && it.media && <BeaconMedia path={it.media} type="video" encKey={it.enc ? encKey : null} iv={it.media_iv} className="w-full max-h-[420px] rounded" />}
          {it.type === "audio" && it.media && <BeaconMedia path={it.media} type="audio" encKey={it.enc ? encKey : null} iv={it.media_iv} />}
          {it.type === "session" && it.session && (
            <a href={it.session.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border px-3 py-2 font-body text-sm mt-1" style={{ borderColor: accent, color: accent }}>
              <Calendar size={14} /> {it.session.when ? new Date(it.session.when).toLocaleString() : "Join session"}
            </a>
          )}
          {it.link_url && (
            <ExternalLinkButton url={it.link_url} label={it.link_label} accent={accent} testid="beacon-item-external-link"
              thumbNode={it.link_thumb ? <BeaconMedia path={it.link_thumb} type="image" className="w-full h-full object-cover" /> : null} />
          )}
          {it.type === "file" && <FileDownload it={it} encKey={encKey} accent={accent} />}
          {it.type !== "artwork" && <LessonComplete it={it} accent={accent} />}
          {it.type !== "artwork" && <LessonNotes it={it} />}
        </>
      )}
      {comments && <BeaconComments bid={comments.bid} item={it} encKey={encKey} accent={accent} userId={comments.userId} canPulse={comments.canPulse} defaultOpen={openComments} />}
    </div>
  );
};

export default function BeaconStorefront() {
  const { handle } = useParams();
  const nav = useNavigate();
  const { loadBeaconKey, status: cryptoStatus } = useCrypto();
  const { user } = useAuth();
  const [b, setB] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [subInfo, setSubInfo] = useState(null);
  const [encKey, setEncKey] = useState(null);
  const [keyPending, setKeyPending] = useState(false);
  const [previewAs, setPreviewAs] = useState("subscriber");
  const [lessonParam] = useState(() => new URLSearchParams(window.location.search).get("lesson"));
  const isOwner = !!b?.is_owner;
  const learn = useLearn(b?.id, !!b?.learning_enabled && !!b?.entitled && !isOwner, encKey);
  const notes = useLessonNotes(b?.id, !!b?.entitled && !isOwner, { title: b?.title, blanks: b?.notes_blanks !== false });
  const [resume, setResume] = useState(null);
  const goResume = (iid) => { setResume(iid); setTimeout(() => document.getElementById(`lesson-${iid}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 350); };
  useEffect(() => { if (b && !isOwner) beaconHit(handle, "visit"); }, [b?.id, isOwner, handle]); // eslint-disable-line react-hooks/exhaustive-deps
  const countLesson = (iid) => { if (!isOwner) beaconHit(handle, "lesson", { extra: iid }); learn?.opened(iid); };
  const load = async () => {
    try { const { data } = await api.get(`/beacon/view/${handle}`); setB(data); } catch { setB(null); }
    try { const { data } = await api.get(`/beacon/${handle}/subscription`); setSubInfo(data); } catch { /* noop */ }
  };
  useEffect(() => { load(); }, [handle]);

  // Once entitled, unwrap our per-Beacon key so encrypted teachings can be read.
  useEffect(() => {
    if (!b || !b.id) return;
    if (!(b.entitled || b.is_owner)) return;
    if (cryptoStatus !== "unlocked") return;
    const hasEnc = !!b.sub_welcome || (b.sections || []).some((s) => s.comments || (s.items || []).some((it) => it.enc && !it.locked));
    if (!hasEnc) return;
    let cancel = false;
    (async () => {
      try {
        const res = await loadBeaconKey(b.id, handle);
        if (cancel) return;
        setEncKey(res.key || null);
        setKeyPending(!res.key && !!res.pending);
      } catch (e) { console.error("Beacon key load failed", e); }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b?.id, b?.entitled, b?.is_owner, cryptoStatus]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("sub") === "success" && p.get("session_id")) {
      const sid = p.get("session_id");
      window.history.replaceState({}, "", `/beacon/view/${handle}`);
      let n = 0;
      const poll = async () => {
        n++;
        try { const { data } = await api.get(`/beacon/subscription/status/${sid}`); if (data.payment_status === "paid") { load(); return; } } catch { /* noop */ }
        if (n < 8) setTimeout(poll, 1500); else load();
      };
      poll();
    } else if (p.get("sub") === "cancel") {
      window.history.replaceState({}, "", `/beacon/view/${handle}`);
    }
  }, [handle]);

  const join = async () => {
    if (subInfo?.subscribable || (b.price_monthly > 0)) {
      setBusy(true);
      const tab = window.open("about:blank", "_blank");
      try { const { data } = await api.post(`/beacon/${handle}/subscribe`, { origin_url: window.location.origin }); if (tab) tab.location.href = data.url; else window.location.href = data.url; }
      catch (e) { if (tab) tab.close(); window.alert(e?.response?.data?.detail || "This creator isn't accepting subscriptions yet."); }
      finally { setBusy(false); }
      return;
    }
    setBusy(true);
    try { await api.post(`/beacon/${b.id}/join`); await load(); } finally { setBusy(false); }
  };

  if (b === undefined) return <div className="flex justify-center py-20"><SignalPulse size={44} /></div>;
  if (!b) return <div className="max-w-2xl mx-auto px-4 py-20 text-center font-body text-zinc-400">This Beacon isn't available.</div>;
  const accent = b.accent_color || "#14A89A";
  const L = beaconLook(b);
  const hFont = L.hFont;
  const hColor = L.hColor;
  const renderItemFor = (it, s) => <ItemView key={it.id} it={it} accent={accent} hFont={hFont} hColor={hColor} encKey={encKey} onEngage={countLesson}
    comments={(b.entitled || b.is_owner) && s.comments && !it.comments_off && !it.locked ? { bid: b.id, userId: user?.id, canPulse: !b.is_owner } : null} openComments={it.id === lessonParam} />;

  return (
    <div className={`relative min-h-screen ${themeClass(b.page_theme)}`} data-testid="beacon-storefront-page" data-theme={b.page_theme || "dark"}>
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[420px]" style={{ background: `radial-gradient(ellipse 75% 100% at 50% 0%, ${accent}22, transparent 72%)` }} />
      <div className="relative max-w-6xl mx-auto px-4 pt-8" data-testid="beacon-storefront-hero">
        <button onClick={() => nav(-1)} className="flex items-center gap-1.5 text-zinc-500 font-body text-xs mb-4 hover:text-white"><ArrowLeft size={14} /> Back</button>
        <BeaconHero b={b} byline={b.creator_name ? `by ${b.creator_name}` : null} />
      </div>
      <div className="relative max-w-3xl mx-auto px-4 pb-8" data-testid="beacon-storefront">
      {b.is_owner && <WelcomePreviewToggle value={previewAs} onChange={setPreviewAs} accent={accent} />}
      {(b.is_owner ? previewAs === "subscriber" : b.entitled)
        ? (b.sub_welcome && <SubscriberWelcome key={previewAs} b={b} handle={handle} accent={accent} encKey={encKey} keyPending={keyPending} />)
        : <VisitorWelcome b={b} accent={accent} onTrailerPlay={() => { if (!b.is_owner) beaconHit(handle, "trailer"); }} />}
      {b.about && <p className="font-body text-zinc-300 leading-relaxed mb-5 whitespace-pre-wrap">{b.about}</p>}

      {!b.entitled && !b.is_owner && (
        <div className="border p-5 mb-6 flex items-center justify-between flex-wrap gap-3" style={{ borderColor: accent + "66", background: "#121212" }} data-testid="beacon-subscribe-bar">
          <span className="font-body text-white">{(subInfo?.subscribable && subInfo?.price_cents) ? `$${(subInfo.price_cents / 100).toFixed(2)}/month` : (b.price_monthly > 0 ? `$${b.price_monthly}/month` : "Free to join")}</span>
          <button onClick={join} disabled={busy} data-testid="beacon-join-btn" className="font-body font-semibold px-5 py-2.5 disabled:opacity-40" style={{ background: accent, color: "#0A0A0A" }}>
            {busy ? "…" : (subInfo?.subscribable || b.price_monthly > 0) ? "Subscribe" : "Join for free"}
          </button>
        </div>
      )}
      {(b.entitled || b.is_owner) && <div className="mb-6 font-body text-sm" style={{ color: accent }} data-testid="beacon-entitled-note"><Flame size={13} className="inline mr-1.5" />{b.is_owner ? "This is your Beacon" : "You have full access"}</div>}

      {keyPending && (
        <div className="border border-[#14A89A]/40 bg-[#14A89A]/5 p-4 mb-6 flex items-start gap-2.5" data-testid="beacon-key-pending">
          <ShieldCheck size={16} className="text-[#14A89A] mt-0.5 shrink-0" />
          <p className="font-body text-sm text-zinc-300">Your access is being prepared. This creator's teachings are encrypted on your device, so they unlock privately once the creator is next active. Check back shortly.</p>
        </div>
      )}

      {(b.entitled || b.is_owner) && <BeaconUpcoming events={b.events} accent={accent} />}

      <ArtCtx.Provider value={{ ownerId: b.owner_id, isOwner, handle, teaserOn: !!b.public_teaser_enabled }}>
      <LearnCtx.Provider value={learn}>
      <NotesCtx.Provider value={notes}>
      <ContinueBar b={b} learn={learn} accent={accent} onGo={goResume} />
      <BeaconSectionCards key={resume || "s"} sections={b.sections || []} look={L} entitled={!!(b.entitled || b.is_owner)}
        renderSection={(s) => {
          if (["grid", "masonry", "single"].includes(s.layout) && s.items.some((it) => it.type === "artwork")) return <ArtGallery section={s} encKey={encKey} accent={accent} renderItem={renderItemFor} />;
          if (["path", "library"].includes(s.layout) || s.items.some((it) => it.type === "file") || ((learn || notes) && s.items.some((it) => it.type !== "artwork"))) return <CourseView section={s} accent={accent} encKey={encKey} renderItem={renderItemFor} beacon={b} />;
          return null;
        }}
        initialOpenId={(b.sections || []).find((s) => s.items.some((it) => it.id === (resume || lessonParam)))?.id || null}
        renderItem={renderItemFor} />
      </NotesCtx.Provider>
      </LearnCtx.Provider>
      </ArtCtx.Provider>
      <BeaconDisclaimer b={b} />
      </div>
    </div>
  );
}
